import { useCallback } from 'react';
import { message } from 'antd';
import { useEditorStore } from '../store/editorStore';
import { useDataStore } from '../store/dataStore';
import { useSearchStore } from '../store/searchStore';
import { useRoutePlanStore } from '../store/routePlanStore';
import type { MapEditorCoordinator } from '../controller/MapEditorCoordinator';

/**
 * RightOperate 业务回调钩子
 *
 * 层级：hooks（"React 生命周期"）
 *
 * 职责：
 * 1. 把右侧面板的编辑、删除、分页、表单搜索、车道线等操作
 *    从 index.tsx 中抽离，集中到本 hook
 * 2. 通过 coordinatorRef 调用 controller / manager 方法
 *
 * 返回的回调引用稳定，可直接传给 RightOperate
 */
export function useRightOperate(
  coordinatorRef: React.MutableRefObject<MapEditorCoordinator | null>,
  getOverlayContainer: () => HTMLElement | null
) {
  /**
   * 分页变化
   */
  const changePageCb = useCallback(
    (current: number, pageSize: number) => {
      useDataStore.getState().setPageData({ current, pageSize });
    },
    []
  );

  /**
   * 点击编辑按钮
   * 路径规划模式下禁止进入编辑绘制流程，提示先退出
   */
  const handleClickEdit = useCallback(
    (val: any) => {
      if (useRoutePlanStore.getState().planningMode) {
        message.warning('请先退出路径规划模式');
        return;
      }
      coordinatorRef.current?.enterEditMode(val);
    },
    [coordinatorRef]
  );

  /**
   * 取消编辑/新增，回到浏览态
   */
  const cancelEdit = useCallback(() => {
    const state = useEditorStore.getState();
    if (state.session.activePolygonData) {
      state.setViewMode(state.session.activePolygonData);
      coordinatorRef.current?.highlightMap(state.session.activePolygonData);
    } else {
      state.setBrowseMode();
    }
  }, [coordinatorRef]);

  /**
   * 重新绘制（新增模式下退出表单时）
   * 路径规划模式下禁止进入绘制流程，提示先退出
   */
  const repaintPolygon = useCallback(() => {
    if (useRoutePlanStore.getState().planningMode) {
      message.warning('请先退出路径规划模式');
      return;
    }
    const ctrl = coordinatorRef.current;
    const draft = useEditorStore.getState().session.draft;
    if (!ctrl || !draft) return;
    // 先切回绘制模式，再清掉旧图形、启动绘制，保证 DrawingStrategy 已激活。
    useEditorStore.setState({
      session: {
        ...useEditorStore.getState().session,
        mode: 'createDrawing',
        activePolygonData: null,
        activeFeature: null,
        popupVisible: false,
      },
    });
    ctrl.repaintDraw(draft);
  }, [coordinatorRef]);

  /**
   * 启用/停用动态地图
   * 与 master 对齐：成功后关闭弹窗、清空选中态并刷新列表
   */
  const updateMapStatusWithReopen = useCallback(
    (item: any) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      ctrl.updateMapStatus(item);
    },
    [coordinatorRef]
  );

  /**
   * 车道线显示/隐藏
   */
  const handleGetLaneCoordinates = useCallback(
    (show: boolean) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      if (show) {
        const feature = useEditorStore.getState().session.draft?.feature;
        if (!feature) return;
        return ctrl.lane.drawByGeometry(ctrl.map.exportGeometry(feature));
      }
      ctrl.lane.clear();
    },
    [coordinatorRef]
  );

  /**
   * 右侧表单搜索/重置
   */
  const changeFormSearchVal = useCallback(
    (type: 'reset' | 'search', val?: any) => {
      const ctrl = coordinatorRef.current;
      if (!ctrl) return;
      const currentPositionSearchVal =
        useSearchStore.getState().positionSearchVal;
      if (
        !currentPositionSearchVal.lon ||
        !currentPositionSearchVal.lat ||
        !currentPositionSearchVal.mapId
      ) {
        if (type === 'search') {
          message.info('请先选择位置信息');
        }
        return;
      }
      ctrl.searchAndRefresh(
        { lon: currentPositionSearchVal.lon, lat: currentPositionSearchVal.lat },
        val
      );
    },
    [coordinatorRef]
  );

  return {
    changePageCb,
    handleClickEdit,
    cancelEdit,
    repaintPolygon,
    updateMapStatusWithReopen,
    handleGetLaneCoordinates,
    changeFormSearchVal,
  };
}
